import { PropsWithChildren } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import DistributeDoorModal from '~/components/Modals/DistributeDoorModal'
import DistributeObstructionModal from '~/components/Modals/DistributeObstructionModal'
import DistributeProductModal from '~/components/Modals/DistributeProductModal'
import RoofTemplatesModal from '~/components/Modals/RoofTemplatesModal'
import ShapeTemplatesModal from '~/components/Modals/ShapeTemplatesModal'
import { DeleteSelectedObjectsButton } from '~/components/Toolbelt/components/DeleteSelectedObjectsButton'
import { DuplicateSelectedObjectsButton } from '~/components/Toolbelt/components/DuplicateSelectedObjectsButton'
import LayerKeys from '~/config/layerKeys'
import ObjectClassName from '~/config/objectClassNames'
import routes from '~/config/routes'
import { Tools } from '~/config/tools'
import { useAppDispatch, useAppSelector } from '~/store/hooks'
import { deleteBackgroundImage } from '~/store/objects'
import { facilityIsRectangle } from '~/store/objects/selectors'
import { showPanel } from '~/store/panel'
import { DETAILED_OBSTRUCTION_PANEL } from '~/store/panel/types'
import { setActiveTool } from '~/store/tools'
import { Button } from '~/ui/Button'
import { Icon } from '~/ui/Icon'
import { DialogTrigger } from '~/ui/Modal'
import { ToggleButton } from '~/ui/ToggleButton'

export const ToolSpecificActions = ({ selectedLayer }: { selectedLayer: LayerKeys | 'MULTIPLE' }) => {
  switch (selectedLayer) {
    case 'BACKGROUND_IMAGE': {
      return <BackgroundImageActions />
    }
    case 'COMFORT_ZONES': {
      return <ComfortZoneActions />
    }
    case 'DIMENSION_LINES':
    case 'DIMENSIONS': {
      return <DimensionActions />
    }
    case 'DOORS': {
      return <DoorActions />
    }
    case 'ROOFS':
    case 'ELEVATION_LINE':
    case 'ELEVATION_POINT': {
      return <RoofActions />
    }
    case 'EXTERIOR_WALLS':
    case 'INTERIOR_WALLS': {
      return <WallActions />
    }
    case 'OBSTRUCTIONS': {
      return <ObstructionActions />
    }
    case 'PRODUCT_HEIGHTS':
    case 'PRODUCTS':
    case 'PRODUCTS_OVERHEAD':
    case 'PRODUCTS_DIRECTIONAL':
    case 'PRODUCTS_HEATERS':
    case 'PRODUCTS_EVAP': {
      return <ProductActions />
    }
    case 'UTILITY_BOXES':
    case 'MULTIPLE': {
      return (
        <>
          <DuplicateSelectedObjectsButton />
          <DeleteSelectedObjectsButton />
        </>
      )
    }
    default: {
      return <></>
    }
  }
}

const ToolbeltLink = ({ to, isDisabled, children }: PropsWithChildren<{ to: string; isDisabled?: boolean }>) => {
  const location = useLocation()
  const navigate = useNavigate()
  return (
    <Button isDisabled={isDisabled} variant="toolbelt" onPress={() => navigate(location.pathname + to)}>
      {children}
    </Button>
  )
}

const ToolbeltToggle = ({ tool, children }: PropsWithChildren<{ tool: Tools }>) => {
  const activeTool = useAppSelector(state => state.tools.activeTool)
  const dispatch = useAppDispatch()
  const setTool = (isSelected: boolean) => {
    if (isSelected) dispatch(setActiveTool({ tool }))
  }
  return (
    <ToggleButton fullWidth isSelected={activeTool === tool} onChange={setTool}>
      {children}
    </ToggleButton>
  )
}

const RoofActions = () => {
  const isRectangularFacility = useAppSelector(facilityIsRectangle)
  return (
    <>
      <ToolbeltToggle tool="ELEVATION_POINT_TOOL">
        <Icon name="elevationPoint" />
        Elev Point
      </ToolbeltToggle>
      <ToolbeltToggle tool="ELEVATION_LINE_TOOL">
        <Icon name="caretUp" />
        Elev Line
      </ToolbeltToggle>
      <DialogTrigger>
        <RoofTemplatesModal />
        <Button isDisabled={!isRectangularFacility} variant="toolbelt">
          <Icon name="plus" />
          Add
        </Button>
      </DialogTrigger>
      <DeleteSelectedObjectsButton />
    </>
  )
}

const ProductActions = () => {
  const selectedObjects = useAppSelector(state => state.selectedObjects)
  const isProductInSelection = !!selectedObjects.find(({ className }) => className === ObjectClassName.PRODUCT)
  return (
    <>
      <DialogTrigger>
        <Button isDisabled={!isProductInSelection} variant="toolbelt">
          <Icon name="grid" />
          Grid
        </Button>
        <DistributeProductModal />
      </DialogTrigger>
      <DuplicateSelectedObjectsButton />
      <DeleteSelectedObjectsButton />
    </>
  )
}

const DoorActions = () => {
  const selectedObjects = useAppSelector(state => state.selectedObjects)
  const isDoorInSelection = !!selectedObjects.find(({ className }) => className === ObjectClassName.DOOR)
  return (
    <>
      <DialogTrigger>
        <DistributeDoorModal />
        <Button isDisabled={!isDoorInSelection} variant="toolbelt">
          <Icon name="grid" />
          Grid
        </Button>
      </DialogTrigger>
      <DuplicateSelectedObjectsButton />
      <DeleteSelectedObjectsButton />
    </>
  )
}

const ComfortZoneActions = () => {
  return (
    <>
      <ToolbeltToggle tool="COMFORT_ZONE_LINE_TOOL">
        <Icon name="draw" />
        Draw
      </ToolbeltToggle>
      <ToolbeltToggle tool="COMFORT_ZONE_TOOL">
        <Icon name="square" />
        Rect
      </ToolbeltToggle>
      <DuplicateSelectedObjectsButton />
      <DeleteSelectedObjectsButton />
    </>
  )
}

const WallActions = () => {
  const currentLayer = useAppSelector(state => state.layers.currentLayer)
  const isExteriorWalls = currentLayer === 'EXTERIOR_WALLS'
  const hasExteriorWalls = useAppSelector(
    ({ objects }) => !!Object.values(objects.present.objects).find(obj => obj.layerKey === 'EXTERIOR_WALLS')
  )
  return (
    <>
      <ToolbeltToggle tool="LINE_TOOL">
        <Icon name="draw" />
        Draw
      </ToolbeltToggle>
      <ToolbeltToggle tool="RECTANGLE_TOOL">
        <Icon name="square" />
        Rect
      </ToolbeltToggle>
      <ToolbeltToggle tool="ELLIPSE_TOOL">
        <Icon name="circle" />
        Circle
      </ToolbeltToggle>
      {isExteriorWalls && (
        <DialogTrigger>
          <ShapeTemplatesModal />
          <Button isDisabled={hasExteriorWalls} variant="toolbelt">
            <Icon name="plus" />
            Add
          </Button>
        </DialogTrigger>
      )}
      <DeleteSelectedObjectsButton />
    </>
  )
}

const ObstructionActions = () => {
  const selectedObjects = useAppSelector(state => state.selectedObjects)
  const isObstructionInSelection = !!selectedObjects.find(({ className }) => className === ObjectClassName.OBSTRUCTION)
  const dispatch = useAppDispatch()
  return (
    <>
      <div className="w-full grid gap-1 grid-cols-1 [@media(max-height:850px)]:grid-cols-2 [@media(max-height:850px)]:[&_button]:text-[10px]">
        <ToolbeltToggle tool="OBSTRUCTION_POLYGON_TOOL">
          <Icon name="draw" />
          Draw
        </ToolbeltToggle>
        <ToolbeltToggle tool="OBSTRUCTION_BOX_TOOL">
          <Icon name="square" />
          Rect
        </ToolbeltToggle>
        <ToolbeltToggle tool="OBSTRUCTION_CIRCLE_TOOL">
          <Icon name="circle" />
          Circle
        </ToolbeltToggle>
        <DialogTrigger>
          <Button isDisabled={!isObstructionInSelection} variant="toolbelt">
            <Icon name="grid" />
            Grid
          </Button>
          <DistributeObstructionModal />
        </DialogTrigger>
        <DuplicateSelectedObjectsButton />
        <DeleteSelectedObjectsButton />
      </div>
      <Button variant="toolbelt" onPress={() => dispatch(showPanel({ type: DETAILED_OBSTRUCTION_PANEL }))}>
        <Icon name="plus" />
        Add
      </Button>
    </>
  )
}

const DimensionActions = () => {
  return (
    <>
      <ToolbeltToggle tool="DIMENSION_TOOL">
        <Icon name="ruler" />
        Measure
      </ToolbeltToggle>
      <DuplicateSelectedObjectsButton />
      <DeleteSelectedObjectsButton />
    </>
  )
}

const BackgroundImageActions = () => {
  const dispatch = useAppDispatch()
  return (
    <>
      <ToolbeltToggle tool="MOVE_TOOL">
        <Icon name="move" />
        Move
      </ToolbeltToggle>
      <ToolbeltLink to="/upload-image/background">
        <Icon name="plus" />
        Add
      </ToolbeltLink>
      <ToolbeltLink to={routes.modals.scaleImage}>
        <Icon name="ruler" />
        Scale
      </ToolbeltLink>
      <ToolbeltLink to={routes.modals.editImage}>
        <Icon name="edit" />
        Crop/Rotate
      </ToolbeltLink>
      <Button variant="toolbelt" onPress={() => dispatch(deleteBackgroundImage())}>
        <Icon name="trash" />
        Delete
      </Button>
    </>
  )
}
